import {
  parseDbTime,
  type EventEvolutionData,
  type TimelineEvent,
} from "@nexus/widget-kit";

export interface MonitoringSegment {
  protectedEventId: number;
  tenant: string;
  channel: string;
  family: string;
  detections: number | null;
  responseRate: number | null;
  responseWeight: number | null;
  detectionTarget: number | null;
  latestBucket: string | null;
  intervalMinutes: number;
  trend: Array<{ at: string; count: number }>;
  anomalies: EventEvolutionData["breakdown"][number]["anomaly_details"];
  engines: string[];
  expectedIsps: number | null;
  detectionStart: string | null;
  responseStart: string | null;
  blockingStart: string | null;
  blockingEnd: string | null;
}
export interface MonitoredEvent extends TimelineEvent {
  performanceState: "ready" | "unavailable" | "scheduled";
  segments: MonitoringSegment[];
}
export interface LiveMonitoringData {
  events: MonitoredEvent[];
  fetchedAt: string;
  from: string;
  to: string;
}

const valid = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

export function summarizeSegment(
  evolution: EventEvolutionData,
  identity: Pick<MonitoringSegment, "protectedEventId" | "tenant" | "channel" | "family">,
): MonitoringSegment {
  const detections = evolution.detections
    .filter((point) => valid(point.cumulative))
    .sort((a, b) => parseDbTime(a.timestamp) - parseDbTime(b.timestamp));
  const responses = evolution.responses
    .filter((point) => valid(point.cumulative))
    .sort((a, b) => parseDbTime(a.timestamp) - parseDbTime(b.timestamp));
  const latest = detections[detections.length - 1];
  const response = responses[responses.length - 1];
  // Rates can only be combined when numerator and denominator share a bucket.
  const matching = response && detections.find((point) => point.timestamp === response.timestamp);
  const targetKnown =
    evolution.breakdown.length > 0 &&
    evolution.breakdown.every(
      (entry) => valid(entry.expected_detection_count) && entry.expected_detection_count > 0,
    );
  return {
    ...identity,
    expectedIsps:
      valid(evolution.expected_isps) && evolution.expected_isps > 0
        ? evolution.expected_isps
        : null,
    detectionStart: evolution.detection_start,
    responseStart: evolution.response_start,
    blockingStart: evolution.blocking_start,
    blockingEnd: evolution.blocking_end,
    detections: latest?.cumulative ?? null,
    responseRate: response?.cumulative ?? null,
    responseWeight:
      matching && valid(evolution.expected_isps) && evolution.expected_isps > 0
        ? matching.cumulative * evolution.expected_isps
        : null,
    detectionTarget: targetKnown
      ? evolution.breakdown.reduce((sum, entry) => sum + entry.expected_detection_count, 0)
      : null,
    latestBucket: latest?.timestamp ?? null,
    intervalMinutes: evolution.interval_minutes,
    trend: detections.slice(-16).map((point) => ({ at: point.timestamp, count: point.cumulative })),
    anomalies: Array.from(
      new Map(
        evolution.breakdown
          .flatMap((entry) => entry.anomaly_details)
          .map((item) => [item.anomaly_id, item]),
      ).values(),
    ),
    engines: Array.from(
      new Set(
        evolution.breakdown.flatMap((entry) =>
          entry.monitoring_engines.map((engine) => engine.engine_label),
        ),
      ),
    ),
  };
}

export function summarizeEvent(event: MonitoredEvent, now: number) {
  const segments = event.segments;
  const known = segments.filter((segment) => segment.detections !== null);
  const responses = segments.filter(
    (segment) => segment.responseRate !== null && (segment.responseWeight ?? 0) > 0,
  );
  const weight = responses.reduce((sum, segment) => sum + segment.responseWeight!, 0);
  const older = segments.some(
    (segment) =>
      segment.latestBucket &&
      now - parseDbTime(segment.latestBucket) > Math.max(15, segment.intervalMinutes * 2) * 60000,
  );
  const targets = segments.filter((segment) => segment.detectionTarget !== null);
  const reached = targets.filter(
    (segment) => segment.detections !== null && segment.detections >= segment.detectionTarget!,
  );
  const representedChannels = new Set(segments.map((segment) => String(segment.protectedEventId)));
  const partial =
    known.length !== segments.length ||
    segments.length === 0 ||
    event.protected_events.some(
      (channel) => !representedChannels.has(String(channel.protected_event_id)),
    );
  const status =
    event.unresolved_anomaly_count > 0
      ? "Needs attention"
      : event.performanceState === "scheduled"
        ? "Upcoming"
        : event.performanceState === "unavailable" || partial
          ? "Data incomplete"
          : older
            ? "Older data"
            : "No open anomalies";
  return {
    detections: known.length ? known.reduce((sum, segment) => sum + segment.detections!, 0) : null,
    responseRate:
      weight > 0
        ? responses.reduce(
            (sum, segment) => sum + segment.responseRate! * segment.responseWeight!,
            0,
          ) / weight
        : null,
    responsePartial: responses.length !== segments.length,
    targets: targets.length,
    reached: reached.length,
    total: segments.length,
    partial,
    older,
    status,
  };
}

/** What a row must signal beyond its figures: open anomalies first, then data quality. */
export function eventSignals(event: MonitoredEvent, now: number) {
  const summary = summarizeEvent(event, now);
  const quality: "partial" | "older" | null =
    event.performanceState === "unavailable" || summary.partial
      ? "partial"
      : summary.older
        ? "older"
        : null;
  return { summary, anomalies: event.unresolved_anomaly_count, quality };
}

/** The analyst's reading order: open anomalies, then doubtful data, then what ends first. */
export function rankLiveEvents(events: MonitoredEvent[], now: number) {
  return events
    .map((event) => ({ event, ...eventSignals(event, now) }))
    .sort(
      (a, b) =>
        b.anomalies - a.anomalies ||
        Number(b.quality !== null) - Number(a.quality !== null) ||
        parseDbTime(a.event.event_end) - parseDbTime(b.event.event_end),
    );
}
